import { AppFooter } from "@/components/home/AppFooter";
import { AppTopNav, PAGE_CONTAINER_ID } from "@/components/home/AppTopNav";
import { Seo } from "@/components/seo/Seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import type { HelpCategoryId, HelpItem } from "@/content/help";
import { CATEGORIES, HELP_ITEMS } from "@/content/help";
import { cn } from "@/lib/utils";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Hash, Lock, Shield, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PRIVACY_PATH, TERMS_PATH } from "./paths";

type CategoryFilter = HelpCategoryId | "all";

function tokenizeQuery(q: string) {
  return q.trim().toLowerCase().split(/\s+/g).filter(Boolean);
}

function matchesQuery(item: HelpItem, q: string) {
  const tokens = tokenizeQuery(q);
  if (tokens.length === 0) return true;

  const haystackParts = [
    item.id,
    ...(item.keywords ?? []),
    ...(item.tags ?? []),
  ]
    .map((s) => s.toLowerCase())
    .filter(Boolean);

  return tokens.every((tok) => haystackParts.some((p) => p.includes(tok)));
}

export function HelpPage() {
  const [activeCategory, setActiveCategory] =
    useState<CategoryFilter>("getting-started");
  const [query, setQuery] = useState("");

  const searchRef = useRef<HTMLInputElement>(null);

  const matchesByCategory = useMemo(() => {
    const map = new Map<HelpCategoryId, HelpItem[]>();
    for (const c of CATEGORIES) map.set(c.id, []);

    for (const item of HELP_ITEMS) {
      if (!matchesQuery(item, query)) continue;
      for (const tag of item.tags ?? []) {
        map.get(tag)?.push(item);
      }
    }
    return map;
  }, [query]);

  const totalMatches = useMemo(() => {
    return HELP_ITEMS.filter((it) => matchesQuery(it, query)).length;
  }, [query]);

  const filteredItems = useMemo(() => {
    if (activeCategory === "all") {
      const seen = new Set<string>();
      return HELP_ITEMS.filter((it) => matchesQuery(it, query)).filter((it) => {
        if (seen.has(it.id)) return false;
        seen.add(it.id);
        return true;
      });
    }

    return (matchesByCategory.get(activeCategory) ?? []).filter((it) =>
      matchesQuery(it, query),
    );
  }, [activeCategory, matchesByCategory, query]);

  const activeMeta = useMemo(() => {
    if (activeCategory === "all") return null;
    return CATEGORIES.find((c) => c.id === activeCategory) ?? null;
  }, [activeCategory]);

  // Auto-switch category when searching and current category has no results
  useEffect(() => {
    const hasQuery = tokenizeQuery(query).length > 0;
    if (!hasQuery || activeCategory === "all") return;

    const currentCount = matchesByCategory.get(activeCategory)?.length ?? 0;
    if (currentCount > 0) return;

    let best: HelpCategoryId | null = null;
    let bestCount = 0;

    for (const c of CATEGORIES) {
      const count = matchesByCategory.get(c.id)?.length ?? 0;
      if (count > bestCount) {
        bestCount = count;
        best = c.id;
      }
    }

    if (best && bestCount > 0) setActiveCategory(best);
  }, [activeCategory, matchesByCategory, query]);

  return (
    <>
      <Seo
        title={t`Help & Support`}
        description={t`Get help using Stratistics and find answers to common questions.`}
        canonicalPath="/help/"
      />
      <div
        id={PAGE_CONTAINER_ID}
        className="min-h-screen max-h-full overflow-y-auto  bg-gray-50/50"
      >
        {/* Header/Navigation */}
        <AppTopNav />

        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          <div className="container mx-auto px-4 pb-10 pt-32 md:pb-14 md:pt-40">
            <div className="mx-auto max-w-4xl">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white md:text-5xl">
                    <Trans>Help</Trans>
                  </h1>
                  <p className="mt-3 max-w-2xl text-lg text-gray-600 dark:text-gray-300">
                    <Trans>Answers to common questions.</Trans>
                  </p>
                </div>
              </div>

              {/* Search + chips */}
              <div className="mt-8 space-y-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <div className="relative w-full md:max-w-xl">
                    <Input
                      ref={searchRef}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={t`Search by keyword…`}
                      aria-label={t`Search help`}
                      className={cn("h-11", query && "pr-10")}
                    />

                    {query ? (
                      <button
                        type="button"
                        onClick={() => {
                          setQuery("");
                          // next frame ensures input sees latest value
                          requestAnimationFrame(() =>
                            searchRef.current?.focus(),
                          );
                        }}
                        aria-label={t`Clear search`}
                        className={cn(
                          "absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2",
                          "text-gray-500 hover:text-gray-900",
                          "hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900",
                        )}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button asChild variant="ghost" size="sm">
                      <Link to={PRIVACY_PATH} className="gap-2">
                        <Lock className="h-4 w-4" />
                        <Trans>Privacy</Trans>
                      </Link>
                    </Button>
                    <Button asChild variant="ghost" size="sm">
                      <Link to={TERMS_PATH} className="gap-2">
                        <Shield className="h-4 w-4" />
                        <Trans>Terms</Trans>
                      </Link>
                    </Button>
                  </div>
                </div>

                {/* Topic chips */}
                <div className="flex flex-wrap gap-2">
                  {/* All */}
                  <TopicChip
                    active={activeCategory === "all"}
                    onClick={() => setActiveCategory("all")}
                    icon={Hash}
                    label={<Trans>All</Trans>}
                    count={totalMatches}
                  />

                  {CATEGORIES.map((c) => {
                    const count = matchesByCategory.get(c.id)?.length ?? 0;
                    if (count === 0) return null;
                    return (
                      <TopicChip
                        key={c.id}
                        active={activeCategory === c.id}
                        onClick={() => setActiveCategory(c.id)}
                        icon={c.icon}
                        label={c.label}
                        count={count}
                      />
                    );
                  })}
                </div>

                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {activeCategory === "all" ? (
                    <Trans>Showing results across all topics.</Trans>
                  ) : activeMeta?.description ? (
                    activeMeta.description
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="border-t border-gray-200 py-10 dark:border-gray-800 dark:bg-gray-900/20">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl space-y-4">
              <Card>
                <CardHeader className="space-y-2">
                  <CardTitle className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      {activeCategory !== "all" && activeMeta?.icon ? (
                        <activeMeta.icon className="h-5 w-5 text-orange-600" />
                      ) : null}
                      <span className="text-gray-900 dark:text-white">
                        {activeCategory === "all" ? (
                          <Trans>All topics</Trans>
                        ) : (
                          (activeMeta?.label ?? <Trans>Help</Trans>)
                        )}
                      </span>
                    </div>
                    <Badge variant="secondary" className="rounded-full">
                      {filteredItems.length} <Trans>items</Trans>
                    </Badge>
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  {filteredItems.length === 0 ? (
                    <div className="rounded-xl border border-dashed p-6 text-sm text-gray-600 dark:border-gray-800 dark:text-gray-300">
                      <Trans>No results.</Trans>{" "}
                      <Trans>Try a different topic or clear your search.</Trans>
                    </div>
                  ) : (
                    <Accordion type="single" collapsible className="w-full">
                      {filteredItems.map((item) => (
                        <AccordionItem key={item.id} value={item.id}>
                          <AccordionTrigger className="text-left">
                            {item.q}
                          </AccordionTrigger>
                          <AccordionContent className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">
                            {item.a}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </CardContent>
              </Card>

              <div className="text-xs text-gray-500 dark:text-gray-400">
                <Trans>
                  Note: Strava API policies and rate limits can affect what
                  third-party apps can access and how quickly data syncs.
                </Trans>
              </div>
            </div>
          </div>
        </section>

        <AppFooter />
      </div>
    </>
  );
}

function TopicChip(props: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  label: React.ReactNode;
  count: number;
}) {
  const Icon = props.icon;

  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-pressed={props.active}
      className={cn(
        "flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900",
        props.active
          ? "border-orange-200 bg-orange-50 text-gray-900 dark:border-orange-900/40 dark:bg-orange-950/20 dark:text-white"
          : "border-gray-200 bg-white/70 text-gray-700 hover:bg-white dark:border-gray-800 dark:bg-gray-900/30 dark:text-gray-200 dark:hover:bg-gray-900/50",
      )}
    >
      <Icon
        className={cn(
          "h-4 w-4",
          props.active ? "text-orange-600" : "text-gray-500 dark:text-gray-300",
        )}
      />
      <span className="font-medium">{props.label}</span>
      <Badge
        variant="secondary"
        className={cn(
          "rounded-full",
          props.active && "bg-white/70 dark:bg-white/10",
        )}
      >
        {props.count}
      </Badge>
    </button>
  );
}
